
/**
 * Module dependencies.
 */

import { Message, MessageType } from '../message';
import { ReactNode, useCallback, useEffect } from 'react';
import styled from 'styled-components';

/**
 * `SnackbarMessageOptions` type.
 */

export type SnackbarMessageOptions = {
  hasTimeout?: boolean;
  timeout?: number;
  type: MessageType;
};

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  id: number;
  onRemove: (id: number) => void;
  options?: SnackbarMessageOptions;
};

/**
 * `StyledMessage` styled component.
 */

const StyledMessage = styled(Message)`
  margin-bottom: 8px;
`;

/**
 * Export `SnackbarMessage` component.
 */

export function SnackbarMessage(props: Props): JSX.Element {
  const { children, id, onRemove, options } = props;
  const { hasTimeout = true, timeout: timeoutValue, type } = options ?? {};
  const handleCloseMessage = useCallback(() => {
    onRemove(id);
  }, [id, onRemove]);

  useEffect(() => {
    if (hasTimeout) {
      const timeout = setTimeout(() => {
        onRemove(id);
      }, timeoutValue ?? 3000);

      return () => clearTimeout(timeout);
    }
  }, [hasTimeout, id, onRemove, options, timeoutValue]);

  return (
    <StyledMessage
      onClose={handleCloseMessage}
      type={type as MessageType}
    >
      {children}
    </StyledMessage>
  );
}
